import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Typography, Button } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { getTripReplacementHistoryV2 } from 'store/tripSliceV2';
import ReplacementHistoryModal from './ReplacementHistoryModal';

const { Title, Text } = Typography;

const HighlightBox = styled.div`
	background-color: #f8f9fa;
	border-radius: 8px;
	padding: 18px 20px;
	border-left: 4px solid #f59e0b;
	margin-bottom: 20px;
`;

const HighlightHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 14px;
`;

const ReplacedLine = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
`;

const Arrow = styled.span`
	color: #94a3b8;
`;

const IST_OFFSET_MINUTES = 330; // UTC+5:30

const formatDateTime = (value) => {
	if (!value) return '';
	const utc = moment.utc(value, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true);
	if (utc.isValid()) {
		return utc.utcOffset(IST_OFFSET_MINUTES).format('DD-MM-YYYY hh:mm A');
	}
	const local = moment(value, ['DD-MM-YYYY hh:mm a', 'DD-MM-YYYY HH:mm'], true);
	return local.isValid() ? local.format('DD-MM-YYYY hh:mm A') : value;
};

// Pick the most recent replacement (vehicle or driver) to surface on the card
const getLatestReplacement = (vehicleReplacements, driverReplacements) => {
	const toEntry = (row, kind) => ({
		kind,
		row,
		ts: moment(
			row?.updated_at,
			[moment.ISO_8601, 'DD-MM-YYYY hh:mm a', 'DD-MM-YYYY HH:mm', 'YYYY-MM-DD HH:mm:ss'],
			false
		),
	});

	const entries = [
		...vehicleReplacements.map((r) => toEntry(r, 'vehicle')),
		...driverReplacements.map((r) => toEntry(r, 'driver')),
	];

	if (!entries.length) return null;

	return entries.reduce((latest, entry) => {
		if (!latest) return entry;
		if (entry.ts.isValid() && latest.ts.isValid()) {
			return entry.ts.isAfter(latest.ts) ? entry : latest;
		}
		return latest;
	}, null);
};

function ReplacementHistoryCard({ tripId, vehicleReplacements, driverReplacements, tripCode }) {
	const dispatch = useDispatch();
	const [modalVisible, setModalVisible] = useState(false);
	const [history, setHistory] = useState(null);

	// Fetch replacement history from the dedicated search API when a trip id is
	// available; fall back to the props passed in from tracking info otherwise.
	useEffect(() => {
		if (!tripId) return;
		dispatch(getTripReplacementHistoryV2({ trip_id: tripId })).then((res) => {
			if (res) {
				setHistory({ vehicle: res.vehicle || [], driver: res.driver || [] });
			}
		});
	}, [tripId, dispatch]);

	const vehicleRows = history?.vehicle ?? vehicleReplacements;
	const driverRows = history?.driver ?? driverReplacements;
	const resolvedTripCode = tripId || tripCode;

	const latest = getLatestReplacement(vehicleRows, driverRows);
	const isDriver = latest?.kind === 'driver';
	const label = isDriver ? 'Driver Replaced' : 'Vehicle Replaced';
	const oldValue = (isDriver ? latest?.row?.old_driver : latest?.row?.old_vehicle_number) || '-';
	const newValue = (isDriver ? latest?.row?.new_driver : latest?.row?.new_vehicle_number) || '-';
	const latestDate = formatDateTime(latest?.row?.updated_at) || '-';

	return (
		<Card
			bodyStyle={{ padding: '0px' }}
			style={{
				borderRadius: '12px',
				marginBottom: '16px',
				border: 'none',
				boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
			}}
		>
			<div style={{ padding: '20px 24px 8px 24px' }}>
				<Title level={4} style={{ margin: 0, fontWeight: 700, color: '#1f1f1f' }}>
					Replacement History
				</Title>
			</div>

			<div style={{ padding: '16px 24px 24px 24px' }}>
				<HighlightBox>
					<HighlightHeader>
						<Text
							strong
							style={{
								fontSize: '12px',
								color: '#f59e0b',
								letterSpacing: '0.8px',
							}}
						>
							LATEST REPLACEMENT
						</Text>
						<Text type="secondary" style={{ fontSize: '13px', color: '#94a3b8' }}>
							{latestDate}
						</Text>
					</HighlightHeader>
					<ReplacedLine>
						<Text strong style={{ fontSize: '14px', color: '#1e293b' }}>
							{label} :
						</Text>
						<Text type="secondary" style={{ color: '#94a3b8' }}>
							{oldValue}
						</Text>
						<Arrow>→</Arrow>
						<Text strong style={{ color: '#1e293b' }}>
							{newValue}
						</Text>
					</ReplacedLine>
				</HighlightBox>

				<Button
					type="primary"
					block
					icon={<ExportOutlined />}
					style={{
						backgroundColor: '#1f3e5a',
						borderColor: '#1f3e5a',
						borderRadius: '6px',
					}}
					onClick={() => setModalVisible(true)}
				>
					View More Details
				</Button>
			</div>

			<ReplacementHistoryModal
				visible={modalVisible}
				tripCode={resolvedTripCode}
				vehicleReplacements={vehicleRows}
				driverReplacements={driverRows}
				onClose={() => setModalVisible(false)}
			/>
		</Card>
	);
}

ReplacementHistoryCard.propTypes = {
	tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	vehicleReplacements: PropTypes.arrayOf(PropTypes.object),
	driverReplacements: PropTypes.arrayOf(PropTypes.object),
	tripCode: PropTypes.string,
};

ReplacementHistoryCard.defaultProps = {
	tripId: undefined,
	vehicleReplacements: [],
	driverReplacements: [],
	tripCode: '',
};

export default ReplacementHistoryCard;
